import React, { useState, useEffect } from "react";
import { useGetOpportunityInfoGather } from "@/api/opportunities/useGetOpportunityInfoGather";
import { toast } from "react-toastify";

import { correlationHeaders } from "@/utils/correlationId";

const BASE_URL = import.meta.env.VITE_BASE_URL;

// Reusable auth helper
const getAuthHeaders = () => {
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const headers = { ...correlationHeaders() };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const OpportunityCoversionTab = ({ oppID, funnel, opprefID, mm_acc_id }) => {
  // ===== Financing (new logic) =====
  const { oppDetails, refetch } = useGetOpportunityInfoGather({ oppID, funnel });

  const [showReassignConfirmationModal, setShowReassignConfirmationModal] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [financingNew, setFinancingNew] = useState({
    approved_amt: 0,
  });

  // ===== Insurance (old logic) =====
  const [insuranceData, setInsuranceData] = useState(null);
  const [isLoadingInsurance, setIsLoadingInsurance] = useState(false);
  const [applicantDetails, setApplicantDetails] = useState(null);
  const [contactDetails, setContactDetails] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const defaultNewRow = {
    providers: "",
    is_takaful: false,
    finalised_sum_insured: "",
    quotation_sent: false,
    quotation_selected: false,
    policy_upload: false,
  };

  const [newRow, setNewRow] = useState(defaultNewRow);

  // ===== Helpers =====
  const formatNumberWithCommas = (value) => {
    if (!value) return "";

    const cleanValue = value.toString().replace(/[^\d.]/g, "");
    const parts = cleanValue.split(".");
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (parts.length > 1) {
      return integerPart + "." + parts[1].substring(0, 2);
    }
    return integerPart;
  };

  const formatCurrency = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      isNaN(parseFloat(value))
    ) {
      return "0.00";
    }
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // =========================
  // Financing handlers
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFinancingNew((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing((prev) => !prev);
  };

  const handleSubmit = () => {
    setShowReassignConfirmationModal(true);
  };

  const handleConfirmationCancel = () => {
    setShowReassignConfirmationModal(false);
  };

  const handleConfirmationSubmit = async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      toast.error("No auth token found. Please log in again.");
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/financing-update-approved?opp_id=${oppID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ ...financingNew }),
        }
      );

      if (response.ok) {
        setIsEditing(false);
        refetch();
        toast.success("Opportunity updated successfully!");
        setShowReassignConfirmationModal(false);
      } else {
        const msg = await response.text();
        console.error("Failed to update opportunity:", response.status, msg);
        toast.error("Failed to update opportunity");
      }
    } catch (error) {
      console.error("Error updating opportunity:", error);
      toast.error("Error updating opportunity");
    }
  };

  // =========================
  // Insurance: fetch functions
  // =========================
  useEffect(() => {
    if (
      (funnel === "Insurance" || funnel === "InsuranceRenewal") &&
      opprefID
    ) {
      fetchInsuranceData();
      fetchInsuranceExtraData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel, opprefID, oppID]);

  const fetchInsuranceData = async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      console.warn("No auth token found – cannot fetch insurance onboarding");
      return;
    }

    setIsLoadingInsurance(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/insurance/onboarding/${oppID}/${funnel}`,
        {
          headers: {
            ...authHeaders,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInsuranceData(data);
      } else {
        console.error("Failed to fetch insurance data");
        setInsuranceData({ onboard: [] });
      }
    } catch (error) {
      console.error("Error fetching insurance data:", error);
      setInsuranceData({ onboard: [] });
    } finally {
      setIsLoadingInsurance(false);
    }
  };

  const fetchInsuranceExtraData = async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      console.warn("No auth token found – cannot fetch insurance extra data");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/get_insur_api/${opprefID}`, {
        headers: {
          ...authHeaders,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setApplicantDetails(data?.applicantDetails || null);
        setContactDetails(data?.contactDetails || null);
      } else {
        console.error("Failed to fetch applicant/contact details");
      }
    } catch (error) {
      console.error("Error fetching applicant/contact details:", error);
    }
  };

  const handleEditRow = (index) => {
    setEditingRow(index);
    setIsAddingNew(false);
  };

  const handleCancelEditRow = () => {
    setEditingRow(null);
    setIsAddingNew(false);
    setNewRow(defaultNewRow);
  };

  const handleSave = async (index, updatedData) => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      toast.error("No auth token found. Please log in again.");
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/insurance/onboarding/${oppID}/${funnel}/${index}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ index, data: updatedData }),
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        await fetchInsuranceData();
        setEditingRow(null);
        toast.success("Insurance policy updated successfully!");
      } else {
        console.error("Failed to update insurance data:", responseData);
        toast.error(
          `Failed to update insurance data: ${
            responseData.error || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error("Error updating insurance data:", error);
      toast.error(`Error updating insurance data: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNew = async (dataToAdd = null) => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      toast.error("No auth token found. Please log in again.");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = dataToAdd || newRow;

      const response = await fetch(
        `${BASE_URL}/api/insurance/onboarding/${oppID}/${funnel}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(dataToSave),
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        await fetchInsuranceData();
        setIsAddingNew(false);
        setNewRow(defaultNewRow);
        toast.success("New insurance policy added successfully!");
      } else {
        console.error("Failed to add new insurance data:", responseData);
        toast.error(
          `Failed to add new insurance data: ${
            responseData.error || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error("Error adding new insurance data:", error);
      toast.error(`Error adding new insurance data: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      toast.error("No auth token found. Please log in again.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this record?")) return;

    setIsSaving(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/insurance/onboarding/${oppID}/${funnel}/${index}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ index }),
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        await fetchInsuranceData();
        toast.success("Insurance policy deleted successfully!");
      } else {
        console.error("Failed to delete insurance data:", responseData);
        toast.error(
          `Failed to delete insurance data: ${
            responseData.error || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error("Error deleting insurance data:", error);
      toast.error(`Error deleting insurance data: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // =========================
  // Inner row components
  // =========================
  const EditableRow = ({
    data,
    index,
    isNew = false,
    oppID,
    opprefID,
    funnel,
    mm_acc_id,
  }) => {
    const [localData, setLocalData] = useState(() => {
      if (isNew) {
        return { ...defaultNewRow };
      }
      return { ...data };
    });

    const [displayValue, setDisplayValue] = useState(() => {
      const initialValue = isNew ? "" : data.finalised_sum_insured || "";
      return formatNumberWithCommas(initialValue);
    });

    const [uploadedFile, setUploadedFile] = useState(null);

    const handleLocalChange = (field, value) => {
      setLocalData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleSumInsuredChange = (e) => {
      const inputValue = e.target.value;
      const cleanValue = inputValue.replace(/[^\d.]/g, "");

      if (cleanValue === "" || /^\d*\.?\d*$/.test(cleanValue)) {
        handleLocalChange("finalised_sum_insured", cleanValue);
        const formatted = formatNumberWithCommas(cleanValue);
        setDisplayValue(formatted);
      }
    };

    const handleSaveClick = async () => {
      if (!localData.providers?.trim()) {
        alert("Provider name cannot be empty");
        return;
      }

      const sumInsured = localData.finalised_sum_insured;
      if (sumInsured === "" || sumInsured === null || sumInsured === undefined) {
        alert("Finalised sum insured cannot be empty");
        return;
      }

      const numericValue = parseFloat(sumInsured);
      if (isNaN(numericValue) || numericValue < 0) {
        alert("Finalised sum insured must be a valid positive number");
        return;
      }

      if (localData.policy_upload && !uploadedFile) {
        alert("Policy PDF file is required when 'Policy Uploaded' is ticked.");
        return;
      }

      const dataToSave = {
        ...localData,
        finalised_sum_insured: numericValue,
      };

      if (isNew) {
        await handleAddNew(dataToSave);
      } else {
        await handleSave(index, dataToSave);

        if (dataToSave.policy_upload) {
          const authHeaders = getAuthHeaders();
          if (!authHeaders.Authorization) {
            toast.error("No auth token found. Please log in again.");
            return;
          }

          const formData = new FormData();
          formData.append("file", uploadedFile);
          formData.append("acc_id", mm_acc_id);
          formData.append("ref_id", opprefID);
          formData.append("funnel", funnel);

          try {
            const res = await fetch(`${BASE_URL}/upload_policy`, {
              method: "POST",
              headers: {
                // don't set Content-Type manually when using FormData
                ...authHeaders,
              },
              body: formData,
            });

            const result = await res.json();

            if (res.ok) {
              alert("Policy file uploaded successfully!");
            } else {
              console.error(result.error);
              alert(`Upload failed: ${result.error}`);
            }
          } catch (err) {
            console.error(err);
            alert("File upload failed. Please try again.");
          }
        }
      }
    };

    return (
      <>
        <tr className="hover:bg-gray-50">
          <td className="p-2 border-b">
            <input
              type="text"
              value={localData.providers || ""}
              onChange={(e) => handleLocalChange("providers", e.target.value)}
              className="w-full p-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter provider name"
              maxLength={100}
            />
          </td>
          <td className="p-2 border-b">
            <select
              value={localData.is_takaful ? "true" : "false"}
              onChange={(e) =>
                handleLocalChange("is_takaful", e.target.value === "true")
              }
              className="w-full p-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="false">Conventional</option>
              <option value="true">Takaful</option>
            </select>
          </td>
          <td className="p-2 border-b">
            <input
              type="text"
              inputMode="decimal"
              value={displayValue}
              onChange={handleSumInsuredChange}
              className="w-full p-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </td>
          <td className="p-2 border-b text-center">
            <input
              type="checkbox"
              checked={localData.quotation_sent || false}
              onChange={(e) =>
                handleLocalChange("quotation_sent", e.target.checked)
              }
              className="w-4 h-4"
            />
          </td>
          <td className="p-2 border-b text-center">
            <input
              type="checkbox"
              checked={localData.quotation_selected || false}
              disabled={!localData.quotation_sent}
              onChange={(e) => {
                if (localData.quotation_sent) {
                  handleLocalChange("quotation_selected", e.target.checked);
                }
              }}
              className="w-4 h-4 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </td>
          <td className="p-2 border-b text-center">
            <input
              type="checkbox"
              checked={localData.policy_upload || false}
              disabled={!localData.quotation_selected}
              onChange={(e) => {
                if (localData.quotation_selected) {
                  handleLocalChange("policy_upload", e.target.checked);
                }
              }}
              className="w-4 h-4 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </td>
          <td className="p-2 border-b text-center">
            <div className="flex gap-1 justify-center">
              <button
                onClick={handleSaveClick}
                disabled={isSaving}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "..." : "Save"}
              </button>
              <button
                onClick={handleCancelEditRow}
                disabled={isSaving}
                className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>

        {localData.policy_upload && (
          <tr>
            <td colSpan="7" className="p-3 border-b text-sm">
              <label className="font-medium text-[#475569]">
                Upload Policy PDF:
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setUploadedFile(e.target.files[0])}
                className="block mt-1 text-sm text-[#475569]"
              />
            </td>
          </tr>
        )}
      </>
    );
  };

  const DisplayRow = ({ data, index }) => (
    <tr className="hover:bg-gray-50">
      <td className="p-4 border-b text-[#475569]">
        {data.providers || "N/A"}
      </td>
      <td className="p-4 border-b text-[#475569]">
        {data.is_takaful ? "Takaful" : "Conventional"}
      </td>
      <td className="p-4 border-b text-[#475569]">
        MYR {formatCurrency(data.finalised_sum_insured)}
      </td>
      <td className="p-4 border-b text-center text-[#475569]">
        {data.quotation_sent ? (
          <span className="text-green-500">✔</span>
        ) : (
          <span className="text-red-500">✖</span>
        )}
      </td>
      <td className="p-4 border-b text-center text-[#475569]">
        {data.quotation_selected ? (
          <span className="text-green-500">✔</span>
        ) : (
          <span className="text-red-500">✖</span>
        )}
      </td>
      <td className="p-4 border-b text-center text-[#475569]">
        {data.policy_upload ? (
          <span className="text-green-500">✔</span>
        ) : (
          <span className="text-red-500">✖</span>
        )}
      </td>
      <td className="p-4 border-b text-center">
        <div className="flex gap-1 justify-center">
          <button
            onClick={() => handleEditRow(index)}
            className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(index)}
            className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );

  // =========================
  // Render
  // =========================
  return (
    <>
      <div className="w-full bg-white p-7 rounded-3xl shadow-lg">
        <div className="flex justify-between items-starts">
          <div className="flex items-center">
            <p className="font-bold text-2xl text-[#1E293B]">Conversion</p>
          </div>

          {funnel === "Financing" && (
            <div className="flex space-x-2">
              {isEditing && (
                <button
                  className="group bg-white text-black p-3 rounded-full border-[1px] border-black hover:bg-gray-200 transition flex items-center justify-center w-12 h-12"
                  onClick={handleCancel}
                  aria-label="Cancel Editing"
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18 18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
              <button
                className="group bg-white text-black p-3 rounded-full border-[1px] border-black hover:bg-yellow-300 transition flex items-center justify-center w-12 h-12"
                onClick={isEditing ? handleSubmit : handleEditClick}
                aria-label={isEditing ? "Submit" : "Edit Lead"}
                type="button"
              >
                {isEditing ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m4.5 12.75 6 6 9-13.5"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"
                    />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Financing section */}
        {funnel === "Financing" && (
          <>
            <div className="flex items-center mt-5">
              <div className="border-b border-gray-200 pb-2 w-full flex">
                <span className="w-1/5 font-bold text-[#475569]">
                  Financing Status
                </span>
                {oppDetails?.status === 4 ? (
                  <span className="w-auto h-[34px] py-2 px-4 gap-2 rounded-full border font-bold items-center justify-center flex bg-[#E7FFD6] border-[#E7FFD6] text-[#116D26]">
                    Approved
                  </span>
                ) : oppDetails?.status === 5 ? (
                  <span className="w-auto h-[34px] py-2 px-4 gap-2 rounded-full border font-bold items-center justify-center flex bg-[#FFEDE9] border-[#FFEDE9] text-[#DD3730]">
                    Rejected
                  </span>
                ) : (
                  <span className="w-auto h-[34px] py-2 px-4 gap-2 rounded-full border font-bold items-center justify-center flex bg-[#E3F3FF] border-[#E3F3FF] text-[#00658E]">
                    Pending
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center mt-5">
              <div className="border-b border-gray-200 pb-2 w-full flex">
                <span className="w-1/5 font-bold text-[#475569]">
                  Approved Amount
                </span>
                {isEditing ? (
                  <div className="flex-1 flex items-center">
                    <span className="text-[#475569] mr-2">MYR</span>
                    <input
                      type="number"
                      name="approved_amt"
                      value={financingNew.approved_amt}
                      onChange={handleChange}
                      className="flex-1"
                    />
                  </div>
                ) : (
                  <span className="flex-1 text-[#475569]">
                    MYR {formatCurrency(oppDetails?.approved_amt || 0)}
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        {/* Insurance onboarding + extra info */}
        {(funnel === "Insurance" || funnel === "InsuranceRenewal") && (
          <>
            <div className="mt-2 flex flex-col gap-4 bg-white shadow-md rounded-lg">
              <div className="flex justify-between items-center p-4">
                <h3 className="text-lg font-semibold text-[#1E293B]">
                  Insurance Onboarding
                </h3>
                {!isAddingNew && editingRow === null && (
                  <button
                    onClick={() => setIsAddingNew(true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    type="button"
                  >
                    Add New Policy
                  </button>
                )}
              </div>

              {isLoadingInsurance ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  <span className="ml-2 text-[#475569]">Loading</span>
                </div>
              ) : (
                <table className="min-w-full bg-white border-b border-gray-200 rounded-lg table-fixed text-sm">
                  <thead>
                    <tr className="text-[#94A3B8] bg-[#E2E8F0]">
                      <th className="p-4 border-b text-start w-[20%]">
                        Insurance Provider
                      </th>
                      <th className="p-4 border-b text-start w-[15%]">
                        Quotation Type
                      </th>
                      <th className="p-4 border-b text-start w-[18%]">
                        Finalised Sum Insured
                      </th>
                      <th className="p-4 border-b text-center w-[10%]">
                        Quotation Sent
                      </th>
                      <th className="p-4 border-b text-center w-[12%]">
                        Quotation Selected
                      </th>
                      <th className="p-4 border-b text-center w-[10%]">
                        Policy Uploaded
                      </th>
                      <th className="p-4 border-b text-center w-[15%]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(insuranceData?.onboard) &&
                    insuranceData.onboard.length > 0 ? (
                      insuranceData.onboard.map((policy, index) =>
                        editingRow === index ? (
                          <EditableRow
                            key={`editing-${index}`}
                            data={policy}
                            index={index}
                            oppID={oppID}
                            opprefID={opprefID}
                            funnel={funnel}
                            mm_acc_id={mm_acc_id}
                          />
                        ) : (
                          <DisplayRow
                            key={`display-${index}`}
                            data={policy}
                            index={index}
                          />
                        )
                      )
                    ) : (
                      !isAddingNew && (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center p-8 text-[#475569] font-medium border-b"
                          >
                            <div className="flex flex-col items-center gap-3">
                              <span>No Insurance Onboarding Details Found</span>
                            </div>
                          </td>
                        </tr>
                      )
                    )}

                    {isAddingNew && (
                      <EditableRow
                        key="new-row"
                        data={newRow}
                        index={-1}
                        isNew={true}
                        oppID={oppID}
                        opprefID={opprefID}
                        funnel={funnel}
                        mm_acc_id={mm_acc_id}
                      />
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <br />

            {(applicantDetails || contactDetails) && (
              <div className="mt-4 p-5 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-[#1E293B] mb-3">
                  Applicant & Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm text-[#475569]">
                  {applicantDetails?.fire?.addressDetails && (
                    <>
                      <p>
                        <span className="font-semibold">Address:</span>{" "}
                        {applicantDetails.fire.addressDetails.address1}
                      </p>
                      <p>
                        <span className="font-semibold">City:</span>{" "}
                        {applicantDetails.fire.addressDetails.city}
                      </p>
                      <p>
                        <span className="font-semibold">Postcode:</span>{" "}
                        {applicantDetails.fire.addressDetails.postcode}
                      </p>
                      <p>
                        <span className="font-semibold">State:</span>{" "}
                        {applicantDetails.fire.addressDetails.state}
                      </p>
                    </>
                  )}

                  {contactDetails && (
                    <>
                      {contactDetails.companyName?.trim() && (
                        <p>
                          <span className="font-semibold">Company Name:</span>{" "}
                          {contactDetails.companyName}
                        </p>
                      )}
                      {contactDetails.authorizedPerson?.trim() && (
                        <p>
                          <span className="font-semibold">
                            Authorized Person:
                          </span>{" "}
                          {contactDetails.authorizedPerson}
                        </p>
                      )}
                      {contactDetails.contactNo?.trim() && (
                        <p>
                          <span className="font-semibold">Phone:</span>{" "}
                          {contactDetails.contactNo}
                        </p>
                      )}
                      {contactDetails.email?.trim() && (
                        <p>
                          <span className="font-semibold">Email:</span>{" "}
                          {contactDetails.email}
                        </p>
                      )}

                      {Array.isArray(contactDetails.altContacts) &&
                        contactDetails.altContacts.length > 0 && (
                          <div className="col-span-2 mt-3">
                            <p className="font-semibold">
                              Alternate Contacts:
                            </p>
                            <ul className="list-disc list-inside text-[#475569] text-sm">
                              {contactDetails.altContacts.map((alt, idx) => (
                                <li key={`alt-${idx}`}>{alt}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showReassignConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center text-center">
          <div className="bg-white p-6 rounded-lg w-1/4">
            <div className="flex justify-center mb-2 pt-6">
              <img src="/icons/edit.svg" className="size-11" alt="edit" />
            </div>

            <h1 className="text-2xl font-bold mb-3">Save & Update Details</h1>
            <span>
              Approved amount will be updated to MYR
              {formatCurrency(financingNew.approved_amt)}
            </span>
            <div className="mt-6 mb-6">
              <button
                className="text-black underline hover:text-blue-800 focus:outline-none"
                onClick={handleConfirmationCancel}
                type="button"
              >
                <b>No, Don&apos;t Update</b>
              </button>
            </div>
            <button
              className="bg-yellow-300 text-black py-4 w-full rounded-3xl hover:bg-yellow-400"
              onClick={handleConfirmationSubmit}
              type="button"
            >
              <b>Save & Update</b>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default OpportunityCoversionTab;